/*
 * useVesselLastPositions
 *
 * Fetch vessel last positions at a shared boundary; refetches when the
 * change feed reports vessel_last_position changes.
 *
 * Output: { lastPositionByVesselId, lastPositions }
 * - lastPositionByVesselId: { [vesselId]: [lon, lat] } (map-centering, unchanged)
 * - lastPositions: full normalized nodes (heading, sog, name, ...) for rendering
 *   latest-position markers without the reported track.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { coreGet } from "@/services/coreApi";
import { useEntityChangeSubscription } from "@/features/changeFeed";

const EMPTY = [];

// Identität erhalten, wenn sich inhaltlich nichts geändert hat: lastPositions
// speist profileOptions (Planner-Profilauswahl!) und Leaflet-Layer — eine
// neue Array-Identität pro Refresh würde beides grundlos zurücksetzen.
function samePositions(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const p = a[i];
    const q = b[i];
    if (
      p.vesselId !== q.vesselId ||
      p.vesselName !== q.vesselName ||
      p.lonlat[0] !== q.lonlat[0] ||
      p.lonlat[1] !== q.lonlat[1] ||
      p.measuredAt !== q.measuredAt ||
      p.headingTrueDeg !== q.headingTrueDeg ||
      p.sogKn !== q.sogKn ||
      p.isStale !== q.isStale
    ) {
      return false;
    }
  }
  return true;
}

export default function useVesselLastPositions({
  accessContext,
  setAccessContext,
}) {
  const [lastPositions, setLastPositions] = useState(EMPTY);
  const [refreshTick, setRefreshTick] = useState(0);

  // Access-Objekte per Ref lesen: ein Token-Refresh ändert die
  // Context-Identität und soll keinen Refetch auslösen (deps unten).
  const accessRef = useRef({ accessContext, setAccessContext });
  accessRef.current = { accessContext, setAccessContext };

  useEntityChangeSubscription(["vessel_last_position"], () =>
    setRefreshTick((t) => t + 1),
  );

  useEffect(() => {
    if (!accessContext?.token) {
      setLastPositions(EMPTY);
      return;
    }

    let cancelled = false;

    const fetchLastPositions = async () => {
      try {
        const data = await coreGet(
          "/vessels/last-positions?include_stale=true",
          accessRef.current.accessContext,
          accessRef.current.setAccessContext,
          ".core /vessels/last-positions error=",
        );
        if (cancelled) return;

        const normalized = [];
        for (const p of data?.positions ?? []) {
          const vesselId = p?.vesselByVesselId?.id;
          const lonlat = p?.lonlat;
          if (
            vesselId == null ||
            !Array.isArray(lonlat) ||
            lonlat.length !== 2 ||
            !Number.isFinite(Number(lonlat[0])) ||
            !Number.isFinite(Number(lonlat[1]))
          ) {
            continue;
          }
          normalized.push({
            vesselId: Number(vesselId),
            vesselName: p?.vesselByVesselId?.name ?? null,
            lonlat: [Number(lonlat[0]), Number(lonlat[1])],
            measuredAt: p?.measuredAt ?? null,
            headingTrueDeg: p?.headingTrueDeg ?? null,
            sogKn: p?.sogKn ?? null,
            isStale: p?.isStale ?? false,
          });
        }
        // Kanonische Reihenfolge: die API garantiert keine stabile Sortierung,
        // ohne sort wäre der samePositions-Vergleich nichtdeterministisch.
        normalized.sort((a, b) => a.vesselId - b.vesselId);
        setLastPositions((prev) => (samePositions(prev, normalized) ? prev : normalized));
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load vessel last positions:", err);
        }
      }
    };

    fetchLastPositions();

    return () => {
      cancelled = true;
    };
  }, [accessContext?.token, refreshTick]);

  const lastPositionByVesselId = useMemo(() => {
    const byId = {};
    for (const p of lastPositions) {
      byId[p.vesselId] = p.lonlat;
    }
    return byId;
  }, [lastPositions]);

  return { lastPositionByVesselId, lastPositions };
}
